/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react';
import * as React from 'react';
import { motion } from 'framer-motion';
import { graphql } from 'gatsby';
import styled from 'styled-components';

import { Text } from '../components/design-system';
import { FAQ } from '../components/global/FAQ';
import { Page } from '../components/global/Page';
import {
  Recommendations,
  type RecommendationTypes
} from '../components/use-cases/Recommendations';
import {
  Left,
  Right,
  StyledH1,
  SubHeading,
  TextWrapper
} from '../components/use-cases/sharedStyles';
import { TextBlock } from '../components/use-cases/TextBlock';
import { UseCaseCategoryGrid } from '../components/use-cases/UseCaseCategoryGrid';
import { type UseCaseItem } from '../components/use-cases/UseCaseGridItem';
import {
  type UseCaseCategory,
  UseCaseNavigation
} from '../components/use-cases/UseCaseNavigation';
import { ANIMATIONS, INTERNAL_LINKS } from '../utils';

const Wrapper = styled(motion.div)`
  width: 100%;
`;

const ContentWrapper = styled.div`
  padding: 0 40px;
  display: flex;
  flex-direction: row;
  width: 100%;
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
  margin: 0 auto 100px auto;
  gap: 60px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    flex-direction: column;
    gap: 0;
  }
`;

const StyledSubHeading = styled(SubHeading)`
  margin-bottom: 10px;
`;

const FAQWrapper = styled.div`
  width: 100%;
  background: transparent;
  position: relative;
  z-index: 1;
`;

const SeeMore = styled.div`
  width: 100%;
  padding: 60px 40px 0;
  position: relative;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    padding: 40px 20px 0;
  }
`;

interface UseCaseCategoryDetails extends UseCaseCategory {
  description: {
    description: string;
  };
  faq?: {
    answer: string | any;
    question: string;
  }[];
  recommendations: RecommendationTypes[];
  seoMetaDescription: string;
  seoTitle: string;
  socialSharePreviewImage?: {
    title: string;
    url: string;
  };
  textBlock: {
    raw: string;
  };
}

interface UseCaseCategoryTemplateProps {
  data: {
    allContentfulUseCase: {
      nodes: UseCaseItem[];
    };
    allContentfulUseCaseCategory: {
      nodes: UseCaseCategory[];
    };
    contentfulUseCaseCategory: UseCaseCategoryDetails;
  };
}

const UseCaseCategoryTemplate: React.ComponentType<UseCaseCategoryTemplateProps> =
  React.memo(({ data }) => {
    const useCases = data.allContentfulUseCase.nodes;
    const thisCategory = data.contentfulUseCaseCategory;

    const sanitizedFAQ = useMemo(() => {
      if (thisCategory.faq) {
        const categoryFAQ = [...thisCategory.faq];

        for (let i = 0; i < categoryFAQ.length; i++) {
          categoryFAQ[i].answer = thisCategory.faq[i].answer.answer;
        }

        return categoryFAQ;
      }
      return [];
    }, [thisCategory.faq]);

    return (
      <Page
        seoProps={{
          title: thisCategory.seoTitle,
          image: thisCategory.socialSharePreviewImage?.url,
          description: thisCategory.seoMetaDescription,
          url: `${INTERNAL_LINKS.Templates}${thisCategory.slug}/`
        }}>
        <Wrapper {...ANIMATIONS.SimpleFadeIn}>
          <ContentWrapper>
            <Left>
              <UseCaseNavigation thisCategory={thisCategory.title} />
            </Left>
            <Right>
              <TextWrapper>
                <StyledSubHeading>
                  <span>TEMPLATES</span>
                </StyledSubHeading>
                <StyledH1 renderAs="h1">{thisCategory.title}</StyledH1>
                <Text size="large">{thisCategory.description.description}</Text>
              </TextWrapper>
              <UseCaseCategoryGrid useCases={useCases} />
            </Right>
          </ContentWrapper>
          <TextBlock
            data={thisCategory.textBlock.raw}
            title={thisCategory.title}
          />
          {thisCategory.recommendations && (
            <SeeMore>
              <Recommendations items={thisCategory.recommendations} />
            </SeeMore>
          )}
          {sanitizedFAQ && (
            <FAQWrapper>
              <FAQ items={sanitizedFAQ} noBkg={true} />
            </FAQWrapper>
          )}
        </Wrapper>
      </Page>
    );
  });

export const pageQuery = graphql`
  query ($category: String!) {
    contentfulUseCaseCategory(title: { eq: $category }) {
      title
      slug
      description {
        description
      }
      slug
      seoTitle
      seoMetaDescription
      socialSharePreviewImage {
        title
        url
      }
      textBlock {
        raw
      }
      recommendations {
        ... on ContentfulUseCaseCategory {
          __typename
          slug
          title
          gridImage {
            url
            title
            gatsbyImageData
          }
          socialSharePreviewImage {
            url
            title
          }
          seoMetaDescription
        }
        ... on ContentfulUseCase {
          __typename
          slug
          name
          author
          category {
            slug
          }
          seoMetaDescription
          gridImage {
            url
            title
            gatsbyImageData
          }
        }
        ... on ContentfulBlogPost {
          __typename
          slug
          date
          title
          author
          seoMetaDescription
          heroImage {
            url
            title
          }
        }
      }
      faq {
        question
        answer {
          answer
        }
      }
    }
    allContentfulUseCaseCategory {
      nodes {
        title
        slug
      }
    }
    allContentfulUseCase(filter: { category: { title: { eq: $category } } }) {
      nodes {
        name
        author
        category {
          title
        }
        featured
        slug
        seoMetaDescription
        gridImage {
          title
          url
          gatsbyImageData
        }
      }
    }
  }
`;

export default UseCaseCategoryTemplate;
